import { WIZARD_TARGET_ATTRIBUTE } from './constants';
import type { WizardDefinition, WizardStep } from './types';

export function resolveWizardAlignTarget(
  step: WizardStep,
  stepTarget: HTMLElement,
): HTMLElement | null {
  const alignTargetId = step.pointerAlignTargetId;
  if (!alignTargetId) {
    return null;
  }

  return (
    queryWizardTarget(alignTargetId) ??
    queryWizardTargetWithin(stepTarget, alignTargetId) ??
    (alignTargetId === step.targetId ? stepTarget : null)
  );
}

export function getWizardAlignRect(element: HTMLElement): {
  centerX: number;
  centerY: number;
  bottom: number;
} {
  const rect = element.getBoundingClientRect();
  return {
    centerX: rect.left + rect.width / 2,
    centerY: rect.top + rect.height / 2,
    bottom: rect.bottom,
  };
}

function isVisibleWizardTarget(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return false;
  }

  const style = window.getComputedStyle(element);
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity) > 0
  );
}

export function queryWizardTarget(targetId: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const selector = `[${WIZARD_TARGET_ATTRIBUTE}="${targetId}"]`;
  const matches = document.querySelectorAll<HTMLElement>(selector);
  for (const element of matches) {
    if (isVisibleWizardTarget(element)) {
      return element;
    }
  }

  return matches[0] ?? null;
}

export function queryWizardTargetWithin(
  root: ParentNode,
  targetId: string,
): HTMLElement | null {
  if (
    root instanceof HTMLElement &&
    root.getAttribute(WIZARD_TARGET_ATTRIBUTE) === targetId
  ) {
    return root;
  }

  return root.querySelector<HTMLElement>(
    `[${WIZARD_TARGET_ATTRIBUTE}="${targetId}"]`,
  );
}

export function findWizardStepIndexWithTarget(
  definition: WizardDefinition,
  startIndex: number,
  direction: 1 | -1,
): number | null {
  const { steps } = definition;
  if (steps.length === 0) {
    return null;
  }

  let index = startIndex;
  while (index >= 0 && index < steps.length) {
    const step = steps[index];
    if (step && queryWizardTarget(step.targetId)) {
      return index;
    }
    index += direction;
  }

  return null;
}

export function findInitialWizardStepIndex(
  definition: WizardDefinition,
): number | null {
  return findWizardStepIndexWithTarget(definition, 0, 1);
}
